import FormText from 'components/form/FormText';
import IndicatorButton from 'components/indicator/IndicatorButton';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';
import { AcctState, AcctStateType } from 'stores/atoms/acctState';

/**
 *　プロフィール設定画面
 */
export default function SettingProfile(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    userName: string;
    tel: string;
    roleDiv: number;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState] = useRecoilState(AcctState);

  const [processing, setProcessing] = useState(false);
  const { updateAcctState, noticeMessageSuccess } = useAppState();
  const { postAcctUpdate } = useApiState();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormStateType>();
  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    _initState(acctState);
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　画面のステートを初期化します。
   */
  const _initState = (state: AcctStateType) => {
    // フォームの初期値設定
    setValue('userName', state.user.name);
    setValue('tel', state.company.tel);
    setValue('roleDiv', state.company.member?.roleDiv);
  };

  /**
   * 保存ボタン押下時の処理
   */
  const onSave = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // カンパニーメンバー更新APIの呼び出し
          await postAcctUpdate({
            companyId: acctState.company.id,
            name: data.userName,
          });

          // アカウント関連のステートを更新
          const newState = await updateAcctState(['user']);
          // 画面のステートを初期化
          _initState(newState);

          // 完了メッセージの表示
          noticeMessageSuccess('プロフィールの保存が完了しました。');
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">{acctState.user.name}さんのプロフィール</h2>
            </div>
            <div className="flex-grow-1 flex-right">
              <ul className="list-inline list-btn mb-0">
                <li className="list-inline-item">
                  <button
                    onClick={onSave}
                    className="btn btn-common btn-common-w147 moderate-blue"
                    disabled={processing}
                  >
                    <IndicatorButton visible={processing} />
                    保存する
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="section-profile">
        <div className="container">
          <div className="box-common">
            <form className="form-profile g-3">
              <div className="row form-row flex-md-row justify-content-between">
                <div className="column col-lg-6 col-xl-6 col-left">
                  <FormText
                    subject="氏名"
                    name="userName"
                    register={register}
                    errors={errors}
                    required={true}
                    maxLength={100}
                    requiredMark={true}
                  />
                </div>
                <div className="column col-lg-6 col-xl-6 col-left">
                  <div className="form-label">ログインメールアドレス</div>
                  <p className="ms-4 fw-bold mb-0">{acctState.user.mailAddress}</p>
                </div>
              </div>
              <div className="row form-row">
                <div className="column col-lg-6 col-xl-6 col-left">
                  <div className="form-label">ユーザー権限</div>
                  <p className="ms-4 fw-bold mb-0">
                    {AppHelper.getDivLabel('role', acctState.company.member?.roleDiv)}
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="section-companies">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">参加しているカンパニー</h2>
            </div>
          </div>
          <div>
            <ul className="list-unstyled list-company d-flex flex-wrap">
              {acctState.user.companies.map(item => {
                return (
                  <li key={`setting-profile-company-${item.id}`} className="me-2">
                    <a key={item.id} href={`/ws/${item.cd}`} className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <img
                          src={AppHelper.getCompanyImageUrl(item.imageKey)}
                          className="icon icon-business img-fluid cropper-img"
                          alt={item.name}
                          width={80}
                        />
                      </div>
                      <p className="flex-grow-1 flex-text mb-0">{item.name}</p>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
